import {
  Text,
  Badge,
  EmptyState,
  GreyGrey80,
  Primary14SemiBold,
  SequenceMenu,
  SequenceMenuItemProps,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  IconBookOpen,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useCreateXeroLedgerAccountMapping } from './hooks/useCreateXeroLedgerAccountMapping'
import { useUpdateXeroLedgerAccountMapping } from './hooks/useUpdateXeroLedgerAccountMapping'
import { useDeleteXeroLedgerAccountMapping } from './hooks/useDeleteXeroLedgerAccountMapping'
import { AddXeroLedgerAccountMappingModal } from './modals/AddXeroLedgerAccountMappingModal'
import { MAPPABLE_LEDGER_ACCOUNT_SECTION } from './xeroLedgerAccountMappings.constants'
import type {
  MappableLedgerAccount,
  XeroLedgerAccountMapping,
  XeroLedgerAccount
} from './xeroLedgerAccountMappings.types'

interface Props {
  mappableLedgerAccount: MappableLedgerAccount
  mappings: XeroLedgerAccountMapping[]
  xeroLedgerAccounts: XeroLedgerAccount[]
  isLoading?: boolean
}

export const LedgerAccountMappingSection = ({
  mappableLedgerAccount,
  mappings,
  xeroLedgerAccounts,
  isLoading
}: Props) => {
  const createMapping = useCreateXeroLedgerAccountMapping()
  const updateMapping = useUpdateXeroLedgerAccountMapping()
  const deleteMapping = useDeleteXeroLedgerAccountMapping()

  const { title, footer } =
    MAPPABLE_LEDGER_ACCOUNT_SECTION[mappableLedgerAccount]

  const existingMappingCodes = mappings.map(m => m.xeroLedgerAccountCode)

  // Clearing is a global account: only one mapping may be configured per account.
  const isSingleMappingAccount = mappableLedgerAccount === 'CLEARING'
  const canAddMapping = !isSingleMappingAccount || mappings.length === 0

  return (
    <Box data-testid={`xeroMapping.section.${mappableLedgerAccount}`}>
      <SettingsGroup
        isLoading={isLoading}
        footer={footer}
        action={
          <Flex
            marginBottom="8px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text as="h3" {...Primary14SemiBold} color={GreyGrey80}>
              {title}
            </Text>
            {canAddMapping && (
              <AddXeroLedgerAccountMappingModal
                mappableLedgerAccount={mappableLedgerAccount}
                xeroLedgerAccounts={xeroLedgerAccounts}
                existingMappingCodes={existingMappingCodes}
                onCreate={createMapping}
                existingMappingCount={mappings.length}
              />
            )}
          </Flex>
        }
      >
        {mappings.length === 0 ? (
          <EmptyState
            title="No mappings configured"
            icon={IconBookOpen}
            iconSize="sm"
            button={
              <AddXeroLedgerAccountMappingModal
                mappableLedgerAccount={mappableLedgerAccount}
                xeroLedgerAccounts={xeroLedgerAccounts}
                existingMappingCodes={existingMappingCodes}
                onCreate={createMapping}
                existingMappingCount={mappings.length}
              />
            }
          />
        ) : (
          mappings.map(mapping => (
            <Box key={mapping.id} data-testid={`xeroMapping.row.${mapping.id}`}>
              <SettingsFormControl
                label={mapping.xeroLedgerAccountName}
                helper={mapping.xeroLedgerAccountCode}
                htmlFor={`xero-mapping-${mapping.id}`}
                icon={
                  <SettingsFormControlIcon>
                    <IconBookOpen />
                  </SettingsFormControlIcon>
                }
              >
                <Flex alignItems="center" gap="8px" paddingLeft="16px">
                  {mapping.isDefault ? (
                    <Badge sentiment="neutral" size="sm">
                      Default
                    </Badge>
                  ) : null}

                  <SequenceMenu
                    data-testid={`xeroMapping.kebab.${mapping.id}`}
                    variant="ghost"
                    buttonSize="small"
                    items={[
                      ...(mapping.isDefault
                        ? []
                        : [
                            (props: Partial<SequenceMenuItemProps>) => (
                              <SequenceMenu.Item
                                data-testid={`xeroMapping.markDefault.${mapping.id}`}
                                key="mark-as-default"
                                uuid="mark-as-default"
                                label="Mark as default"
                                onClick={() => {
                                  void updateMapping({
                                    id: mapping.id,
                                    body: { isDefault: true }
                                  })
                                  return props?.setIsOpen?.(false)
                                }}
                              />
                            )
                          ]),
                      (props: Partial<SequenceMenuItemProps>) => (
                        <SequenceMenu.Item
                          data-testid={`xeroMapping.remove.${mapping.id}`}
                          key="remove"
                          uuid="remove"
                          label="Remove"
                          destructive
                          onClick={() => {
                            void deleteMapping(mapping.id)
                            return props?.setIsOpen?.(false)
                          }}
                        />
                      )
                    ]}
                  />
                </Flex>
              </SettingsFormControl>
            </Box>
          ))
        )}
      </SettingsGroup>
    </Box>
  )
}
